import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  History, Loader2, AlertTriangle, Lock, Share2, Clock,
  Users, DollarSign, Swords, TrendingUp, ChevronDown, ChevronUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from "recharts";

interface Conflict {
  id: string;
  name: string;
  region: string;
  startYear: number;
  endYear: number | null;
  durationMonths: number;
  casualties: number;
  displaced: number;
  economicCostBillions: number;
  peakIntensity: "Low" | "Medium" | "High" | "Extreme";
  tags: string[];
  description: string;
}

const CONFLICTS: Conflict[] = [
  {
    id: "ukraine",
    name: "Russia-Ukraine War",
    region: "Europe",
    startYear: 2022,
    endYear: null,
    durationMonths: 48,
    casualties: 500000,
    displaced: 14000000,
    economicCostBillions: 900,
    peakIntensity: "Extreme",
    tags: ["Active", "Interstate"],
    description: "Full-scale invasion by Russia; largest conventional war in Europe since WWII.",
  },
  {
    id: "iran-war",
    name: "US-Israel-Iran War",
    region: "Middle East",
    startYear: 2026,
    endYear: null,
    durationMonths: 1,
    casualties: 5000,
    displaced: 2000000,
    economicCostBillions: 150,
    peakIntensity: "Extreme",
    tags: ["Active", "Regional"],
    description: "Escalation from Israel-Hamas conflict into direct US-Iran confrontation.",
  },
  {
    id: "gaza",
    name: "Israel-Gaza (2023-25)",
    region: "Middle East",
    startYear: 2023,
    endYear: 2025,
    durationMonths: 20,
    casualties: 45000,
    displaced: 1900000,
    economicCostBillions: 60,
    peakIntensity: "Extreme",
    tags: ["Asymmetric"],
    description: "Post-Oct 7 Israeli military operation in Gaza Strip.",
  },
  {
    id: "syria",
    name: "Syrian Civil War",
    region: "Middle East",
    startYear: 2011,
    endYear: 2024,
    durationMonths: 156,
    casualties: 610000,
    displaced: 13000000,
    economicCostBillions: 1200,
    peakIntensity: "Extreme",
    tags: ["Civil War", "Proxy"],
    description: "Multi-faction civil war with international intervention from Russia, US, Turkey and Iran.",
  },
  {
    id: "yemen",
    name: "Yemen Civil War",
    region: "Middle East",
    startYear: 2014,
    endYear: null,
    durationMonths: 132,
    casualties: 377000,
    displaced: 4500000,
    economicCostBillions: 180,
    peakIntensity: "High",
    tags: ["Active", "Proxy"],
    description: "Houthi insurgency vs Saudi-led coalition; worst humanitarian crisis.",
  },
  {
    id: "sudan",
    name: "Sudan Civil War",
    region: "Africa",
    startYear: 2023,
    endYear: null,
    durationMonths: 33,
    casualties: 150000,
    displaced: 10700000,
    economicCostBillions: 50,
    peakIntensity: "High",
    tags: ["Active", "Civil War"],
    description: "Conflict between SAF and RSF; massive displacement crisis.",
  },
  {
    id: "iraq",
    name: "Iraq War",
    region: "Middle East",
    startYear: 2003,
    endYear: 2011,
    durationMonths: 104,
    casualties: 461000,
    displaced: 4700000,
    economicCostBillions: 2400,
    peakIntensity: "Extreme",
    tags: ["Concluded", "Interstate"],
    description: "US-led invasion and subsequent insurgency/civil war.",
  },
  {
    id: "afghanistan",
    name: "Afghanistan War",
    region: "Central Asia",
    startYear: 2001,
    endYear: 2021,
    durationMonths: 240,
    casualties: 241000,
    displaced: 5900000,
    economicCostBillions: 2300,
    peakIntensity: "High",
    tags: ["Concluded", "COIN"],
    description: "US-led intervention post-9/11; ended with Taliban takeover.",
  },
];

const INTENSITY_COLORS: Record<string, string> = {
  Low: "bg-green-500/20 text-green-400",
  Medium: "bg-yellow-500/20 text-yellow-400",
  High: "bg-orange-500/20 text-orange-400",
  Extreme: "bg-red-500/20 text-red-400",
};

function formatNumber(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(0)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toString();
}

function formatCost(b: number): string {
  return `$${b}B`;
}

export default function HistoricalConflictComparison() {
  const { user, isPremium } = useAuth();
  const [selectedConflicts, setSelectedConflicts] = useState<string[]>(["ukraine", "iran-war", "syria"]);
  const [showAI, setShowAI] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const toggleConflict = (id: string) => {
    setSelectedConflicts((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : prev.length < 4 ? [...prev, id] : prev
    );
  };

  const selected = CONFLICTS.filter((c) => selectedConflicts.includes(c.id));

  const barData = selected.map((c) => ({
    name: c.name.length > 18 ? c.name.slice(0, 18) + "…" : c.name,
    Casualties: c.casualties,
    Displaced: c.displaced,
  }));

  const costData = selected.map((c) => ({
    name: c.name.length > 18 ? c.name.slice(0, 18) + "…" : c.name,
    "Cost ($B)": c.economicCostBillions,
  }));

  // Radar normalization
  const maxCasualties = Math.max(...selected.map((c) => c.casualties));
  const maxDisplaced = Math.max(...selected.map((c) => c.displaced));
  const maxCost = Math.max(...selected.map((c) => c.economicCostBillions));
  const maxDuration = Math.max(...selected.map((c) => c.durationMonths));
  const intensityMap = { Low: 25, Medium: 50, High: 75, Extreme: 100 };

  const radarData = [
    { axis: "Casualties", ...Object.fromEntries(selected.map((c) => [c.id, Math.round((c.casualties / maxCasualties) * 100)])) },
    { axis: "Displaced", ...Object.fromEntries(selected.map((c) => [c.id, Math.round((c.displaced / maxDisplaced) * 100)])) },
    { axis: "Cost", ...Object.fromEntries(selected.map((c) => [c.id, Math.round((c.economicCostBillions / maxCost) * 100)])) },
    { axis: "Duration", ...Object.fromEntries(selected.map((c) => [c.id, Math.round((c.durationMonths / maxDuration) * 100)])) },
    { axis: "Intensity", ...Object.fromEntries(selected.map((c) => [c.id, intensityMap[c.peakIntensity]])) },
  ];

  const radarColors = ["hsl(var(--primary))", "hsl(0 70% 55%)", "hsl(45 80% 55%)", "hsl(200 70% 55%)"];

  // AI analysis query
  const { data: aiAnalysis, isLoading: aiLoading, refetch: fetchAI } = useQuery({
    queryKey: ["conflict-comparison-ai", selectedConflicts.sort().join(",")],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("conflict-comparison-ai", {
        body: { conflicts: selected.map((c) => ({ name: c.name, startYear: c.startYear, endYear: c.endYear, casualties: c.casualties, displaced: c.displaced, economicCostBillions: c.economicCostBillions, region: c.region })) },
      });
      if (error) throw error;
      return data?.analysis as string;
    },
    enabled: false,
  });

  const handleAI = () => {
    setShowAI(true);
    fetchAI();
  };

  const handleShare = () => {
    const names = selected.map((c) => c.name).join(" vs ");
    const text = `Historical Conflict Comparison: ${names}\n\n${selected.map((c) => `${c.name}: ${formatNumber(c.casualties)} casualties, ${formatNumber(c.displaced)} displaced, ${formatCost(c.economicCostBillions)} cost`).join("\n")}\n\nAnalyze global conflicts at`;
    const url = window.location.origin;
    if (navigator.share) {
      navigator.share({ title: "Conflict Comparison", text, url }).catch(() => {});
    } else {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text + " " + url)}`, "_blank");
    }
  };

  const visibleConflicts = expanded ? CONFLICTS : CONFLICTS.slice(0, 4);

  return (
    <section className="py-8 border-t border-primary/10">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <History className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Historical Conflict Comparison</h2>
            <p className="text-xs text-muted-foreground">Compare crises by duration, casualties, displacement & economic cost</p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleShare} className="ml-auto gap-1.5">
            <Share2 className="h-3.5 w-3.5" /> Share
          </Button>
        </div>

        {/* Conflict selector */}
        <div className="mb-6">
          <p className="text-xs text-muted-foreground mb-2">Select up to 4 conflicts to compare:</p>
          <div className="flex flex-wrap gap-2">
            {visibleConflicts.map((c) => {
              const isSelected = selectedConflicts.includes(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggleConflict(c.id)}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all border ${
                    isSelected
                      ? "border-primary bg-primary/15 text-primary"
                      : "border-primary/10 bg-card/50 text-muted-foreground hover:border-primary/30"
                  }`}
                >
                  <Swords className="h-3 w-3" />
                  {c.name}
                  {c.endYear === null && (
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                  )}
                </button>
              );
            })}
          </div>
          {CONFLICTS.length > 4 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline"
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {expanded ? "Show less" : `Show all ${CONFLICTS.length} conflicts`}
            </button>
          )}
        </div>

        {selected.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">Select at least one conflict above to start comparing.</p>
        )}

        {selected.length > 0 && (
          <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border border-primary/10 bg-card/50 p-3 text-center">
                <Clock className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                <p className="text-lg font-bold font-mono text-foreground">{Math.max(...selected.map((c) => c.durationMonths))} mo</p>
                <p className="text-[10px] text-muted-foreground">Longest duration</p>
              </div>
              <div className="rounded-lg border border-primary/10 bg-card/50 p-3 text-center">
                <Users className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                <p className="text-lg font-bold font-mono text-foreground">{formatNumber(selected.reduce((s, c) => s + c.casualties, 0))}</p>
                <p className="text-[10px] text-muted-foreground">Total casualties</p>
              </div>
              <div className="rounded-lg border border-primary/10 bg-card/50 p-3 text-center">
                <TrendingUp className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                <p className="text-lg font-bold font-mono text-foreground">{formatNumber(selected.reduce((s, c) => s + c.displaced, 0))}</p>
                <p className="text-[10px] text-muted-foreground">Total displaced</p>
              </div>
              <div className="rounded-lg border border-primary/10 bg-card/50 p-3 text-center">
                <DollarSign className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                <p className="text-lg font-bold font-mono text-foreground">{formatCost(selected.reduce((s, c) => s + c.economicCostBillions, 0))}</p>
                <p className="text-[10px] text-muted-foreground">Total economic cost</p>
              </div>
            </div>

            {/* Charts row */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Bar chart — Human toll */}
              <div className="rounded-lg border border-primary/10 bg-card/50 p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3">Human Toll Comparison</h3>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData} layout="vertical" margin={{ left: 90, right: 10, top: 5, bottom: 5 }}>
                      <XAxis type="number" tickFormatter={formatNumber} tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={85} />
                      <Tooltip formatter={(v: number) => formatNumber(v)} />
                      <Bar dataKey="Casualties" fill="hsl(0 70% 50%)" />
                      <Bar dataKey="Displaced" fill="hsl(var(--primary) / 0.6)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Radar chart */}
              <div className="rounded-lg border border-primary/10 bg-card/50 p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3">Multi-Dimensional Comparison</h3>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                      <PolarGrid stroke="hsl(var(--primary) / 0.15)" />
                      <PolarAngleAxis dataKey="axis" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} />
                      <PolarRadiusAxis tick={false} axisLine={false} />
                      {selected.map((c, i) => (
                        <Radar
                          key={c.id}
                          name={c.name}
                          dataKey={c.id}
                          stroke={radarColors[i]}
                          fill={radarColors[i]}
                          fillOpacity={0.15}
                          strokeWidth={2}
                        />
                      ))}
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-3 justify-center mt-1">
                  {selected.map((c, i) => (
                    <span key={c.id} className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <span className="h-2 w-2 rounded-full" style={{ background: radarColors[i] }} />
                      {c.name.length > 20 ? c.name.slice(0, 20) + "…" : c.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Economic cost chart */}
            <div className="rounded-lg border border-primary/10 bg-card/50 p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Economic Cost (USD Billions)</h3>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={costData} margin={{ left: 90, right: 10, top: 5, bottom: 5 }} layout="vertical">
                    <XAxis type="number" tickFormatter={(v) => `$${v}B`} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={85} />
                    <Tooltip formatter={(v: number) => `$${v}B`} />
                    <Bar dataKey="Cost ($B)" fill="hsl(45 80% 50%)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Detail table */}
            <div className="rounded-lg border border-primary/10 bg-card/50 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-primary/10 bg-muted/30">
                      <th className="text-left px-3 py-2 font-semibold text-foreground">Conflict</th>
                      <th className="text-center px-3 py-2 font-semibold text-foreground">Period</th>
                      <th className="text-center px-3 py-2 font-semibold text-foreground">Duration</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground">Casualties</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground">Displaced</th>
                      <th className="text-right px-3 py-2 font-semibold text-foreground">Cost</th>
                      <th className="text-center px-3 py-2 font-semibold text-foreground">Intensity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.map((c) => (
                      <tr key={c.id} className="border-b border-primary/5 hover:bg-muted/20">
                        <td className="px-3 py-2">
                          <div className="font-medium text-foreground">{c.name}</div>
                          <div className="text-[10px] text-muted-foreground">{c.region}</div>
                        </td>
                        <td className="text-center px-3 py-2 font-mono text-muted-foreground">
                          {c.startYear}–{c.endYear ?? "now"}
                        </td>
                        <td className="text-center px-3 py-2 font-mono text-muted-foreground">{c.durationMonths} mo</td>
                        <td className="text-right px-3 py-2 font-mono font-bold text-foreground">{formatNumber(c.casualties)}</td>
                        <td className="text-right px-3 py-2 font-mono text-muted-foreground">{formatNumber(c.displaced)}</td>
                        <td className="text-right px-3 py-2 font-mono text-foreground">{formatCost(c.economicCostBillions)}</td>
                        <td className="text-center px-3 py-2">
                          <Badge variant="outline" className={`text-[10px] ${INTENSITY_COLORS[c.peakIntensity]}`}>
                            {c.peakIntensity}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* AI Analysis — Premium */}
            <div className="rounded-lg border border-primary/10 bg-card/50 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                  🧠 AI Comparative Analysis
                  <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">Premium</Badge>
                </h3>
                {isPremium && !showAI && (
                  <Button size="sm" variant="outline" onClick={handleAI} className="text-xs">
                    Generate Analysis
                  </Button>
                )}
              </div>

              {!isPremium ? (
                <div className="flex flex-col items-center gap-2 py-6 text-center">
                  <Lock className="h-6 w-6 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Upgrade to Premium for AI-powered comparative analysis</p>
                </div>
              ) : showAI ? (
                aiLoading ? (
                  <div className="flex items-center gap-2 justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Analyzing conflicts…
                  </div>
                ) : aiAnalysis ? (
                  <div className="prose prose-sm prose-invert max-w-none text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">
                    {aiAnalysis}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-destructive py-4">
                    <AlertTriangle className="h-4 w-4" /> Failed to generate analysis
                  </div>
                )
              ) : null}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
